import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ExpLocalStorage } from './explocalstorage.service';
import { ApiService } from './api.service';
import {
  EsperienzeListConfig, Experience, Language,
  SchedaIntense,
  SchedeIntenseListConfig,
  Word,
  AlertsListConfig,
  Alert,
  Attachment, Stop, Item, Ost, User, SearchConfig, GisQureyResult, OstListConfig
} from '../models';
import { type } from 'os';

@Injectable()
export class WMAIApiService {

  timeout: number = 5000;
  distance: number = 1000;

  @Output() selectedExperience = new EventEmitter<Experience>();
  @Output() selectedItem = new EventEmitter<Item>();
  @Output() selectedOst = new EventEmitter<Ost>();

  constructor(
    private httpClient: HttpClient,
    private apiService: ApiService,
    private expService: ExpLocalStorage
  ) { }

  private formatErrors(error: any) {
    return throwError(error);
  }
  /**
   * si ottiene le impostazioni del profilo
   */
  getProfileSettings(): Observable<Experience[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/profilo').pipe(map(response => response['data']));
  }
  /**
   * si aggiorna il profilo utente con il parametro passato
   * @param  {User} user
   */
  profileUpdate(user: User) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/profiloUpdate?locale=' + user.locale +
      '&agerange=' + user.agerange +
      '&gender=' + user.gender +
      '&nationality=' + user.nationality +
      '&cc=' + user.cc +
      '&privacy=' + user.privacy +
      '&alert=' + user.alert +
      '&news=' + user.news +
      '&email=' + user.email
    ).pipe(map(response => response['data']));
  }
  /**
   * si attiva l'utente
   * @param  {number} id
   * @param  {string} uuid
   */
  activateUser(id: number, uuid: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/activateUser?id=' + id + '&uuid=' + uuid).pipe(map(response => response));
  }
  /**
   * si elimina un'esperienza privata
   */
  deletePrivateExperiences() {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/deletePrivateExperiences').pipe(map(response => response));
  }
  /**
   *si elimina un'esperienza pubblica
   */
  deletePublicExperiences() {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/deletePublicExperiences').pipe(map(response => response));
  }
  /**
   * si elimina un utente
   */
  deleteUser() {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/deleteUser').pipe(map(response => response));
  }
  /** 
   * si aggiorna il primo login di un utente attraverso il parametro
   * @param  {number} id
   */
  updateFirstLogin(id: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateFirstLogin?id=' + id).pipe(map(response => response));
  }
  /** 
   * si invia una mail per il reset della password all'indirizzo email specificato
   * @param  {string} userid
   */
  sendResetPassword(userid: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/sendResetPasswordMail?userid=' + userid).pipe(map(response => response));
  }
  /** 
 * si registra un nuovo dispositivo mobile per l'utente loggato
 * @param  {string} deviceid
 */
  addDevice(deviceid: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/adddevice?deviceid=' + deviceid).pipe(map(response => response['data']));
  }
  /** 
 * si rimuove un dispositivo mobile per l'utente loggato
 * @param  {string} deviceid
 */
  removeDevice(deviceid: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/removedevice?deviceid=' + deviceid).pipe(map(response => response));
  }
  /**
   * attraverso i parametri si vanno a richiedere delle informazioni per quando riguarda la configurazione dell'esperienza
   * @param  {EsperienzeListConfig} config
   * @param  {string} username
   */
  queryProfileExperiences(config: EsperienzeListConfig, username: string): Observable<Object> {
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/profiloExperiencesPreview?username=' + username + '&' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /**
   * si chiedono il numero delle esperienze private
   * @param  {string} username
   */
  queryCountExperiencesPrivate(username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiencesPrivate?username=' + username).pipe(map(response => response['data']));
  }
  /**
   * si chiedono il numero delle esperienze eliminate
   * @param  {string} username
   */
  queryCountExperiencesDelete(username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiencesDelete?username=' + username).pipe(map(response => response['data']));
  }
  /**
   * si chiedono il numero delle esperienze pubbliche
   * @param  {string} username
   */
  queryCountExperiencesPublic(username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiencesPublic?username=' + username).pipe(map(response => response['data']));
  }
  /**
   * si chiedono il numero delle esperienze in attesa di validazione
   * @param  {string} username
   */
  queryCountExperiencesWait(username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiencesWait?username=' + username).pipe(map(response => response['data']));
  }
  /**
   * si chiedono il numero delle esperienze archiviate (cancellazione soft)
   * @param  {string} username
   */
  queryCountExperiencesArchived(username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiencesArchived?username=' + username).pipe(map(response => response['data']));
  }
  /**
   * si chiedono il numero delle esperienze condivise
   * @param  {string} username
   */
  queryCountExperiencesShared(username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiencesShared?username=' + username).pipe(map(response => response['data']));
  }
  /**
   * si chiedono le esperienze ricevute
   * @param  {number} id
   */
  experienceReceiversById(id: number): Observable<User[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceReceiversById?id=' + id).pipe(map(response => response['data']));
  }
  /**
   * si setta la lingua
   * @param  {string} language
   * @param  {string} username
   */
  setLanguage(language: string, username: string): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/profiloUpdateLanguage?username=' + username + '&language=' + language).pipe(map(response => response['data']));
  }
  /**
   * si chiedono il numero delle esperienze dell'admin
   * @param  {EsperienzeListConfig} config
   */
  queryAdminExperiences(config: EsperienzeListConfig): Observable<Object> {
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/adminExperiencesPreview?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /**
   * si chiedono le statistiche dell'admin
   */
  queryAdminStatistics(): Observable<Object> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/adminStatistics').pipe(map(response => response['data']));
  }
  /**
   * si chiedono le statistiche dell'user
   */
  queryUserStatistics(): Observable<Object> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/userStatistics').pipe(map(response => response['data']));
  }
  /**
   * si chiedono il conteggio dell'esperienze
   */
  queryCountExperiences(): Observable<any[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/countExperiences').pipe(map(response => response['data']));
  }
  /**
   * si ottengono tutte le lingue
   */
  getLanguages(): Observable<Language[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/languages').pipe(map(response => response['data']));
  }
  /** si ottiene il limite di intervallo con i parametri passati
   * @param  {string} table
   * @param  {string} column
   */
  getRangeLimit(table: string, column: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/rangelimit?table=' + table + '&column=' + column).pipe(map(response => response['data']));
  }
  /** si cerca la parola attraverso il parametro passato
   * @param  {string} keyword
   */
  getWords(keyword: string): Observable<Word[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/words?keyword=' + keyword).pipe(map(response => response['data']));
  }
  /**
   * si cerca la parola in locale con i parametri passati
   * @param  {string} locale
   * @param  {string} keyword
   */
  getWordsByKeywordLocale(locale: string, keyword: string): Observable<Word[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/wordsbyKeyLocale?locale=' + locale + '&keyword=' + keyword).pipe(map(response => response['data']));
  }
  /**
   * si cerca la parola in locale con i parametri passati
   * @param  {string} locale
   * @param  {string} keyword
   */
  getWordsByLocale(locale: string, keyword: string): Observable<Word[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/wordsbyKeyLocale?locale=' + locale + '&keyword=' + keyword).pipe(map(response => response['data']));
  }
  /**
   * si cerca la parola in locale con i parametri passati
   * @param  {string} locale
   * @param  {string} keyword
   */
  wordsByLocale(locale: string, keyword: string): Observable<Word[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/wordsbylocale?locale=' + locale + '&keyword=' + keyword).pipe(map(response => response['data']));
  }
  /**
   * si ottiene l'OStSingle attraverso i parametri
   * @param  {number} id
   * @param  {string} locale
   */
  ostSingleById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostSinglebyId?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**
   * si ottiene l'OStSPreview attraverso i parametri
   * @param  {string} locale
   */
  ostPreview(locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostPreview?locale=' + locale).pipe(map(response => response['data']));
  }
  /**
   * si recupera l'ultimo OSt
   * @param  {OstListConfig} config
   */
  public queryLatestOst(config: OstListConfig): Observable<Object> {
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostPreview?' + params.substring(1, params.length)).pipe(map(response => response['data']));
  }

  //inizio chiamate parziali a popolamento OST
  /**
   * si chiede l'accessibilità di OSt attraverso i parametri
   * @param  {number} id
   * @param  {string} locale
   */
  ostAccessibilitaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostAccessibilitaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**
 * si chiede l'ambito di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostAmbitoById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostAmbitoById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**
 * si chiedono i contatti di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostContattiById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostContattiById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**
 * si chiede l'esposizione di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostEsposizioneById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostEsposizioneById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si chiede la fruizione di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostFruizioneById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostFruizioneById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si chiede la Protezione di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostProtezioneById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostProtezioneById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si chiede i riconoscimenti di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostRiconoscimentiById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostRiconoscimentiById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si chiede la segnaletica di OSt attraverso i parametri
   * @param  {number} id
   * @param  {string} locale
   */
  ostSegnaleticaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostSegnaleticaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si chiede lo stato fondo di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostStatoFondoById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostStatoFondoById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si chiede il tipo fondo di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostTipoFondoById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostTipoFondoById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si chiede la tipologia di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostTipologiaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostTipologiaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si chiede le immagini di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostImmaginiById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostImmaginiById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si chiede gli allegati di OSt attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  ostAllegatiById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostAllegatiById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  //fine
  /** si richiede le ultime esperienze attravero il parametro
    * @param  {EsperienzeListConfig} config
    */
  public queryLatestEsperienze(config: EsperienzeListConfig): Observable<Object> { // Convert any filters over to Angular's URLSearchParams
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experiencesPreviewLatest?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /** si richiedono tutte le esperienze attraverso il parametro
   * @param  {EsperienzeListConfig} config
   */
  public queryEsperienze(config: EsperienzeListConfig): Observable<Object> { // Convert any filters over to Angular's URLSearchParams
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experiencesPreview?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /** si richiede la singola esperienza attraverso i parametri
   * @param  {number} idesperienza
   * @param  {string} locale
   */
  public querySingleEsperienza(idesperienza: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceSinglebyId?id=' + idesperienza + '&locale=' + locale).pipe(map(response => response));
  }
  /** si richiedono le esperienze fermate attraverso i parametri
   * @param  {number} idesperienza
   * @param  {string} locale
   */
  public queryEsperienzaStops(idesperienza: number, locale: string): Observable<Item[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceStopsById?id=' + idesperienza + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiedono esperienze item attraverso i parametri
   * @param  {number} idesperienza
   * @param  {string} locale
   * @param  {string} stop
   */
  public queryEsperienzaItemsByStop(idesperienza: number, locale: string, stop: string): Observable<Item[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceItemsByStop?id=' + idesperienza + '&locale=' + locale + '&stop=' + stop).pipe(map(response => response['data']));
  }
  /** si richiede l'esperienza in dettaglio attraverso i parametri
   * @param  {number} idesperienza
   * @param  {string} locale
   */
  public queryEsperienzaDetailsById(idesperienza: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceDetailsById?id=' + idesperienza + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiede il file/i file dell'esperienza attraverso i parametri
   * @param  {number} idesperienza
   * @param  {string=''} type
   */
  public queryEsperienzaFiles(idesperienza: number, type: string = ''): Observable<Attachment[]> {
    const path = `/${idesperienza}`
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/resource?path=' + path + '&id=' + localStorage.getItem('id')).pipe(map(response => response['data']));
    // return this.httpClient.get(`${
    //   environment.wmaiAPIServer
    //   }` + '/experienceFilesByExpId?id=' + idesperienza + '&type=' + type).pipe(map(response => response['data']));
  }
  /**si richiede il file/i file dell'esperienza item attraverso i parametri
   * @param  {number} fk_item
   * @param  {number} fk_experience
   */
  public queryEsperienzaItemFiles(fk_item: number, fk_experience: number): Observable<Attachment[]> {
    const path = `/${fk_experience}/${fk_item}`
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/resource?path=' + path + '&id=' + localStorage.getItem('id')).pipe(map(response => response['data']));
    // return this.httpClient.get(`${
    //   environment.wmaiAPIServer
    //   }` + '/experienceFilesByItemId?fk_item=' + fk_item + "&fk_experience=" + fk_experience).pipe(map(response => response['data']));
  }
  /** si richiede  l'esperienza item attraverso i parametri
   * @param  {number} id
   * @param  {string} locale
   */
  public queryEsperienzaItemById(id: number, locale: string): Observable<Item> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceItemById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiedono  i file dell'item attraverso i parametri
   * @param  {number} id
   */
  public queryItemFilesById(id: number): Observable<Attachment[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/itemFilesById?id=' + id).pipe(map(response => response['data']));
  }
  /** si carica  l'allegato all'item
   * @param  {number} item_id
   * @param  {number} fk_experience
   * @param  {string} uploadname
   * @param  {string} ext
   */
  public uploadAttachmentToItem(item_id: number, fk_experience: number, uploadname: string, ext: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/uploadAttachmentToItem?item_id=' + item_id + '&fk_experience=' + fk_experience + '&uploadedname=' + uploadname + '&ext=' + ext).pipe(map(response => response['data']));
  }
  /** si rimuove l'allegato dall'item
   * @param  {number} id
   */
  public removeAttachmentFromItem(id: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/removeAttachmentFromItem?id=' + id).pipe(map(response => response['data']));
  }
  /** si chiede l'item attraverso il parametro
   * @param  {number} id
   */
  public queryItemById(id: number): Observable<Item[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/itemSinglebyId?id=' + id).pipe(map(response => response['data']));
  }
  /** si richiedere OSt item attraverso i parametri
   * @param  {number} idost
   * @param  {string} locale
   */
  public queryOstByItemOstId(idost: number, locale: string): Observable<Ost> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaOstById?idost=' + idost + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si ottiene OSt item attraverso i parametri
   * @param  {any} bbox
   * @param  {string} locale
   */
  public getOstsByBBox(bbox: any, locale: string): Observable<Ost[]> {
    let params = "";
    Object.keys(bbox).forEach((key) => {
      params += "&" + key + "=" + bbox[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostDownloads?' + params.substring(1, params.length) + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si crea l'esperienza attraverso i parametri
   * @param  {number} idscheda
   * @param  {string} locale
   */
  public createEsperienza(idscheda: number, locale: string) {
    return this.apiService.post('/createExperience?idscheda=' + idscheda + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si aggiunge un item con i seguenti parametri
   * @param  {number} experience_id
   * @param  {string} tappa
   * @param  {string} name
   * @param  {string} content
   * @param  {string} geom
   */
  public addItemToEsperienza(experience_id: number, tappa: string, name: string, content: string, geom: string) {
    return this.apiService.post('/insert_ost?experience_id=' + experience_id + '&tappa=' + tappa + '&name=' + name + '&content=' + content + '&geom=' + geom);
  }
  /** si elimina l'item dall'esperienza attraverso i parametri
   * @param  {number} experience_id
   * @param  {number} id
   */
  public delItemToEsperienza(experience_id: number, id: number) {
    return this.apiService.post('/delete_ost?experience_id=' + experience_id + '&id=' + id)
  }
  /** si cancellano le esperienze pubbliche collegate all'esperienza l'item dall'esperienza attraverso il parametro
   * @param  {string} ids
   */
  public rejectEsperienzeCorrelate(ids: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/revokeExperiences?ids=' + ids).pipe(map(response => response['data']));
  }
  /** si rende pubblica l'esperienza selezionata attraverso i parametri
   * @param {number} id
   * @param {number} idscheda
   * @param {number} fkuser
   */
  public updateExperienceToPublic(id: number, idscheda: number, fkuser: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperienceStatusToPublic?id=' + id + '&scheda_id=' + idscheda + '&fk_users=' + fkuser).pipe(map(response => response['data']));
  }
  /** si clona una esperienza selezionata
   * @param {number} id
   */
  public cloneExperience(id: number, status: 'wait') {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/cloneExperience?experience_id=' + id + '&status=' + status).pipe(map(response => response['data']));
  }
  /** si respinge la condivisione dell'esperienza
   * @param {number} id
   * @param {number} idscheda
   * @param {number} fkuser
   */
  public rejectExperience(id: number, idscheda: number, fkuser: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperienceStatusToReject?id=' + id + '&scheda_id=' + idscheda + '&fk_users=' + fkuser).pipe(map(response => response['data']));
  }
  /** si elimina l'esperienza
   * @param  {number} id
   * @param  {number} idscheda
   * @param  {string} fkuser
   */
  deleteExperience(id: number, idscheda: number, fkuser: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperienceStatusToDelete?id=' + id + '&scheda_id=' + idscheda + '&fk_users=' + fkuser).pipe(map(response => response['data']));
  }
  /** si abilita nuovamente una esperienza al suo download
   * @param  {number} id
   * @param  {string} fkuser
   */
  undownloadedExperience(id: number, fkuser: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperienceStatusToUndownloaded?id=' + id + '&fk_users=' + fkuser).pipe(map(response => response['data']));
  }
  /** esperienza aggiornata
   * @param  {number} id
   * @param  {number} idscheda
   * @param  {string} fkuser
   * @param  {string} title
   * @param  {string} content
   */
  public updateEsperienza(id: number, idscheda: number, fkuser: string, title: string, content: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperience?id=' + id + '&scheda_id=' + idscheda + '&fk_users=' + fkuser + '&title=' + title + '&content=' + content).pipe(map(response => response['data']));
  }
  /** esperienza item aggiornata
     * @param  {number} id
     * @param  {number} experience_id
     * @param  {string} title
     * @param  {string} content
     */
  public updateEsperienzaItem(id: number, experience_id: number, title: string, content: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateItemById?id=' + id + '&experience_id=' + experience_id + '&title=' + title + '&content=' + content).pipe(map(response => response['data']));
  }
  /** geometria dell'item aggiornata
   * @param  {number} id
   * @param  {any} geom
   */
  public updateItemGeometry(id: number, geom: any) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateItemGeometryById?id=' + id + '&geom=' + geom).pipe(map(response => response['data']));
  }
  /** condiviso nel privato
   * @param  {number} id
   * @param  {string} receiver
   * @param  {string} status
   */
  public shareToPrivate(id: number, receiver: string, status: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/shareExperienceTo?id=' + id + '&receiver=' + receiver + '&status=' + status).pipe(map(response => response['data']));
  }
  /** esperienza archiviata
   * @param  {number} id
   * @param  {number} idscheda
   * @param  {string} fkuser
   */
  public archiveEsperienza(id: number, idscheda: number, fkuser: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperienceStatusToArchived?id=' + id + '&scheda_id=' + idscheda + '&fk_users=' + fkuser).pipe(map(response => response['data']));
  }
  /**ripristino dell'esperienza privata a seguito di una cancellazione soft
   * @param  {number} id
   * @param  {number} idscheda
   * @param  {string} fkuser
   */
  public recoverEsperienza(id: number, idscheda: number, fkuser: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateExperienceStatusToPrivate?id=' + id + '&scheda_id=' + idscheda + '&fk_users=' + fkuser).pipe(map(response => response['data']));
  }
  /** si ricerca l'esperienza tra le preview attraverso 
   * @param  {SearchConfig} config
   */
  public querySearchEsperienze(config: SearchConfig) {
    let params = "";

    Object.keys(config).forEach((key) => {
      params += "&" + key + "=" + config[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experiencesPreview?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /** si iniva un messaggio alert
   * @param  {number} id
   * @param  {string} type
   * @param  {} params
   */
  sendAlertToRas(id: number, type: string, params) {
    var newParams = '';

    Object.keys(params).forEach((key) => {
      newParams += "&" + key + "=" + params[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/sendAlert?id=' + id + '&type=' + type + newParams).pipe(map(response => response));
  }
  /** si invia una mail relativa al rifiuto di pubblicazione
   * @param  {} id_receiver
   * @param  {} message
   * @param  {} id_operator
   */
  sendRejectFromRas(id_receiver, message, id_operator) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/sendRejectFromRas?id_receiver=' + id_receiver + '&message=' + message + '&id_operator=' + id_operator).pipe(map(response => response));
  }
  /** si invia una mail di approvazione di pubblicazione
   * @param  {} id_receiver
   * @param  {} id_operator
   */
  sendApprovedFromRas(id_receiver, id_operator) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/sendApprovedFromRas?id_receiver=' + id_receiver + '&id_operator=' + id_operator).pipe(map(response => response));
  }
  /** si invia una mail relativa alla condivisione di una esperienza tra privati
   * @param  {string} sender
   * @param  {string} receiver
   * @param  {number} id
   */
  sendSharedExperience(sender: string, receiver: string, id: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/sendSharedExperience?sender=' + sender + '&receiver=' + receiver + '&id=' + id).pipe(map(response => response));
  }
  /** si aggiunge un like all'esperienza
   * @param  {number} share
   * @param  {number} id
   */
  share(id: number) {
    return this.apiService.post('/addShare?id=' + id).pipe(map(response => response['data']));
  }
  /** si rimuove un like all'esperienza
   * @param  {number} share
   * @param  {number} id
   */
  unshare(id: number, share: number) {
    return this.apiService.delete('/delShare?id=' + id + '&share=' + share).pipe(map(response => response['data']));
  }

  private handleErrorObs(error: any) {
    return Observable.throw(error.message || error);
  }

  /*
  * SCHEDE INTENSE
  */
  /**si ricercano le schede 
  * @param  {SchedeIntenseListConfig} config
  */
  public querySearchIntense(config: SearchConfig) {
    let params = "";

    Object.keys(config).forEach((key) => {
      params += "&" + key + "=" + config[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedePreview?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /**si richiedono le ultime schede 
   * @param  {SchedeIntenseListConfig} config
   */
  public queryLatestSchede(config: SchedeIntenseListConfig): Observable<Object> {
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedePreviewLatest?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /** si ricercano le schede intense
   * @param  {SchedeIntenseListConfig} config
   */
  public querySchedeIntense(config: SchedeIntenseListConfig): Observable<{ schedeintense: SchedaIntense[], count: number }> {
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedePreview?' + params.substring(1, params.length)).pipe(map(response => response['data']));
  }
  /** si ricercano gli OST
   * @param  {OstListConfig} config
   */
  public queryOst(config: OstListConfig): Observable<Object> {
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostPreview?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /** si ricercano gli OST
   * @param  {OstListConfig} config
   */
  public querySearchOst(config: SearchConfig) {
    let params = "";

    Object.keys(config).forEach((key) => {
      params += "&" + key + "=" + config[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/ostPreview?' + params.substring(1, params.length)).pipe(map(response => response));
  }
  /** si aggiunge un item dall'OSt
   * @param  {string} tappa
   * @param  {number} id_exp
   * @param  {number} id_ost
   */
  public addItemFromOst(tappa: string, id_exp: number, id_ost: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/addItemFromOst?tappa=' + tappa + '&experience_id=' + id_exp + '&ost_id=' + id_ost).pipe(map(response => response['data']));
  }
  /** si controlla la tappa
   * @param  {string} tappa
   * @param  {number} id_exp
   * @param  {number} id_ost
   */
  public checkTappa(tappa: string, id_exp: number, id_ost: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/checkTappa?tappa=' + tappa + '&experience_id=' + id_exp + '&ost_id=' + id_ost).pipe(map(response => response['data']));
  }

  //inizio chiamate parziali a popolamento di scheda
  /** si richiede la difficoltà della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaDifficoltaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaDifficoltaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiede la fruizione della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaFruizioneById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaFruizioneById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiede la rete della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaReteById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaReteById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiede la segnaletica della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaSegnaleticaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaSegnaleticaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiede lo stato fondo della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaStatoFondoById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaStatoFondoById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiede il tipo fondo della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaTipoFondoById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaTipoFondoById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiede la tematica della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaTematicaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaTematicaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiede la tipologia della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaTipologiaById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaTipologiaById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiedono le immagini della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaImmaginiById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaImmaginiById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiedono gli allegati della scheda attraverso i parametri
 * @param  {number} id
 * @param  {string} locale
 */
  schedaAllegatiById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaAllegatiById?id=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  //fine chiamate
  /** si richiede la singola scheda intensa
   * @param  {number} idscheda
   * @param  {string} locale
   */
  public querySingleSchedaIntense(idscheda: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaPreviewById?idscheda=' + idscheda + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiede la scheda OSt attraverso i parametri
   * @param  {number} id
   * @param  {string} tappa
   * @param  {string} locale
   */
  public querySchedaOstsByTappa(id: number, tappa: string, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaOstsByTappa?idscheda=' + id + '&tappa=' + tappa + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richidono le tappe della scheda attraverso i parametri
   * @param  {number} idscheda
   * @param  {string} locale
   */
  public querySchedaTappeByIdScheda(idscheda: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaTappeById?idscheda=' + idscheda + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** si richiede la scheda intense collegata all'OSt attraverso i parametri
   * @param  {string} ids
   * @param  {string} locale
   */
  public querySchedaOstById(id: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/schedaOstById?idost=' + id + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /**si richiede la lista dell'esperienze pubbliche collegate alla scheda intese attraverso i parametri
   * @param  {number} idscheda
   * @param  {string} locale
   */
  public queryListEsperienzeById(idscheda: number, locale: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/experienceListPublicbySIId?idscheda=' + idscheda + '&locale=' + locale).pipe(map(response => response['data']));
  }
  /** 
   * @param  {string} keyword
   */
  public wordsByKey(keyword: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/wordsbykey?keyword=' + keyword)
      .pipe(
        map(response => response['data'])
      );
  }
  /** si richiedono tutti gli utenti admin
   * @param  {string} userid
   * @param  {string[]} role?
   * @param  {number} pageSize?
   * @param  {number} pageNumber?
   */
  public queryUsers(userid: string, role?: string[], pageSize?: number, pageNumber?: number) {
    var stringRoles = "";
    if (role.length != 0) {
      stringRoles = role.toString();
    }
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/adminUsers?userid=' + userid + '&role=' + stringRoles + '&pageSize=' + pageSize + '&pageNumber=' + pageNumber)
      .pipe(
        map(response => response)
      );
  }
  /** si aggiorna il ruolo dell'utente
   * @param  {number} userid
   * @param  {string} role
   */
  public updateRole(userid: number, role: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/changeUserRole?userid=' + userid + '&role=' + role)
      .pipe(
        timeout(this.timeout),
        map(response => response['data'])
      );
  }
  /** si disabilita il ruolo
   * @param  {string} userid
   */
  public disableUser(userid: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/disableUser?userid=' + userid)
      .pipe(
        timeout(this.timeout),
        map(response => response['data'])
      );
  }
  /** si abilita l'utente
   * @param  {string} userid
   */
  public enableUser(userid: string) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/enableUser?userid=' + userid)
      .pipe(
        timeout(this.timeout),
        map(response => response['data'])
      );
  }
  /**
   * @param  {number} userid
   */
  public profiloAttivo(userid: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/profiloAttivo?userid=' + userid)
      .pipe(
        timeout(this.timeout),
        map(response => response['data'])
      );
  }
  /** invio email della prima login
   * @param  {string} email
   * @param  {string} uuid
   * @param  {number} id
   */
  public sendFirstLoginMail(email: string, uuid: string, id: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/firstLoginMail?email=' + email + '&uuid=' + uuid + '&id=' + id)
      .pipe(
        map(response => response)
      );
  }
  /** alert  di inserimento
   * @param  {Alert} config
   */
  public insertAlert(config: Alert) {
    let params = "";

    Object.keys(config).forEach((key) => {
      params += "&" + key + "=" + config[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/insertAlert?' + params.substring(1, params.length)).pipe(map(response => response['data']));

  }
  /** ottieni tutti gli alert
 * @param  {number} pageNumber
 */
  public getAllAlerts(pageNumber: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/allAlerts?pageNumber=' + pageNumber).pipe(map(response => response));

  }
  /** si elimina l'ealert attravero il parametro
   * @param  {number} id
   */
  public deleteAlert(id: number) {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/deleteAlert?id=' + id).pipe(map(response => response));
  }
  /** si aggiorna l'alert attraverso il parametro
   * @param  {Alert} config
   */
  public updateAlert(config: Alert) {
    let params = "";
    // console.log(config);
    Object.keys(config).forEach((key) => {
      params += "&" + key + "=" + config[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/updateAlert?' + params.substring(1, params.length)).pipe(map(response => response['data']));
  }
  /** si richiedono gli ultimi alert
   * @param  {AlertsListConfig} config
   */
  public queryLatestAlerts(config: AlertsListConfig): Observable<Alert[]> { // Convert any filters over to Angular's URLSearchParams
    let params = "";

    Object.keys(config.filters).forEach((key) => {
      params += "&" + key + "=" + config.filters[key];
    });

    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/alertsLatest?' + params.substring(1, params.length)).pipe(map(response => response['data']));
  }
  /** si richiede la lista di tipi
   * @param  {string} locale
   */
  public queryListTypes(locale: string): Observable<Word[]> {
    return this.httpClient.get(`${
      environment.wmaiAPIServer
      }` + '/getListTypes?locale=' + locale).pipe(map(response => response['data']));
  }
  /** si effettua una query spaziale per bounding box e lingua al fine di ottenere schede intense, esperienze ed ost
   * @param  {number} xmin
   * @param  {number} ymin
   * @param  {number} xmax
   * @param  {number} ymax
   * @param  {string} locale
   */
  public queryGisByExtent(xmin: number, ymin: number, xmax: number, ymax: number, locale: string): Observable<GisQureyResult[]> {
    return this.httpClient.get(
      environment.wmaiAPIServer
      + '/wquery?locale=' + locale
      + '&xmin=' + xmin
      + '&ymin=' + ymin
      + '&xmax=' + xmax
      + '&ymax=' + ymax).pipe(map(response => response['data']));
  }
  /** permette di continuare a lavoare offline e di recuperare il tutto dopo che il servizio ritorna online
   * @param  {number} lat
   * @param  {number} long
   */
  public queryOfflineOSTByCoord(lat: number, long: number): Observable<Ost[]> {
    let selectedOsts: Observable<Ost[]>;
    let osts = this.expService.getLocalOst();
    // console.log("QO");
    // console.log(osts);
    if (osts != null) {
      let ost = osts[0];

      let strgeom = ost.geom.toString();
      let init = strgeom.lastIndexOf("(");
      let last = strgeom.lastIndexOf(")");
      let geom = strgeom.substring(init, last);
      // console.log(geom);

    }
    return selectedOsts;
  }
  /** si calcola una distanza
   * @param  {} lat1
   * @param  {} lon1
   * @param  {} lat2
   * @param  {} lon2
   */
  private calcDist(lat1, lon1, lat2, lon2) {
    var R = 6371; // km
    var dLat = this.toRad(lat2 - lat1);
    var dLon = this.toRad(lon2 - lon1);
    var latitude1 = this.toRad(lat1);
    var latitude2 = this.toRad(lat2);

    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(latitude1) * Math.cos(latitude2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;
    return d;
  }
  /** radice quadrata
   * @param  {} Value
   */
  private toRad(Value) {
    return Value * Math.PI / 180;
  }

  getResouceDownload(name: string){
    return this.httpClient.get(environment.wmaiAPIServer + '/resource/download?file=' + name).pipe(map(response => response['data']));
  }


  public loginIdm(code): Observable<any> {
    return this.httpClient.get(environment.authEndPoint.responseUrl + '/' + code);
  }

  getMapLegend(){
    return this.httpClient.get(environment.wmaiAPIServer + '/wordsbykey?keyword=legenda').pipe(timeout(this.timeout), map(response => response['data']));
  }

}
